import React, { useEffect, useRef } from 'react';
import { 
  FaMotorcycle, 
  FaCar, 
  FaOilCan, 
  FaBatteryFull,
  FaTachometerAlt,
  FaSnowflake,
  FaClipboardCheck,
  FaShieldAlt,
  FaArrowRight,
  FaStar,
  FaWrench,
  FaPhoneAlt
} from 'react-icons/fa';
import { GiGearHammer, GiCarKey, GiSteeringWheel } from 'react-icons/gi';
import { MdElectricalServices, MdAirlineSeatReclineNormal } from 'react-icons/md';

const ServicesSection = () => {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const cardsRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-up');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (headerRef.current) observer.observe(headerRef.current);
    cardsRef.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  const services = [
    {
      id: 1,
      title: 'Bike Servicing',
      icon: <FaMotorcycle />,
      description: 'Complete bike maintenance including engine tune-up, oil change, and chain adjustment.',
      features: ['Engine Tuning', 'Oil Change', 'Brake Check', 'Chain Lubrication'],
      popular: true,
    },
    {
      id: 2,
      title: 'Car Servicing',
      icon: <FaCar />,
      description: 'Full car care solution with professional inspection and quality service.',
      features: ['Engine Check', 'Oil & Filter', 'Brake Service', 'Tire Rotation'],
      popular: true,
    },
    {
      id: 3,
      title: 'Engine Repair',
      icon: <GiGearHammer />,
      description: 'Expert engine diagnostics and repair for all bike and car models.',
      features: ['Diagnostics', 'Overhauling', 'Parts Replacement', 'Performance Test'],
      popular: false,
    },
    {
      id: 5,
      title: 'Electrical Works',
      icon: <MdElectricalServices />,
      description: 'Full electrical system repair including wiring, lights, and sensors.',
      features: ['Wiring Repair', 'Light Fixing', 'Sensor Calibration', 'Battery Check'],
      popular: false,
    },
  ];

  return (
    <section
      ref={sectionRef}
      className="relative w-full overflow-hidden bg-gradient-to-br from-gray-50 to-white py-10 md:py-16"
    >
      {/* Light Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-red-100/30 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-red-50/40 rounded-full filter blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-red-50/20 via-transparent to-transparent"></div>
      </div>

      <div className="container mx-auto px-4 md:px-8 relative z-10">
        {/* Header Section */}
        <div
          ref={headerRef}
          className="text-center mb-12 md:mb-16 opacity-0"
        >
          {/* Title */}
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            <span className="text-gray-800">Our </span>
            <span className="text-red-600 relative inline-block">
              Services
              <span className="absolute bottom-0 left-0 w-full h-1 bg-red-500 rounded-full"></span>
            </span>
          </h2>

          {/* Subtitle */}
          <p className="text-gray-600 max-w-2xl mx-auto text-sm md:text-base">
            We provide comprehensive maintenance and repair services for all types of bikes and cars. 
            Professional care for your vehicle at affordable prices.
          </p>

          {/* Divider */}
          <div className="flex justify-center mt-6">
            <div className="w-16 h-1 bg-red-500 rounded-full"></div>
          </div>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {services.map((service, index) => (
            <div
              key={service.id}
              ref={(el) => (cardsRef.current[index] = el)}
              className="group relative bg-white rounded-2xl p-5 border border-gray-200 hover:border-red-300 transition-all duration-500 hover:-translate-y-2 hover:shadow-xl hover:shadow-red-100/50 opacity-0"
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              {/* Popular Badge */}
              {service.popular && (
                <div className="absolute -top-3 right-4 bg-red-500 text-white text-xs px-3 py-1 rounded-full flex items-center gap-1 shadow-lg">
                  <FaStar className="text-yellow-400 text-xs" />
                  Popular
                </div>
              )}

              {/* Icon */}
              <div className="w-14 h-14 bg-gradient-to-br from-red-50 to-red-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 group-hover:bg-red-100">
                <div className="text-red-500 text-3xl group-hover:text-red-600 transition-colors">
                  {service.icon}
                </div>
              </div>

              {/* Title */}
              <h3 className="text-gray-800 font-bold text-lg mb-2 group-hover:text-red-600 transition-colors">
                {service.title}
              </h3>

              {/* Description */}
              <p className="text-gray-600 text-xs leading-relaxed mb-3">
                {service.description}
              </p>

              {/* Features List */}
              <div className="flex flex-wrap gap-1.5 mb-4">
                {service.features.map((feature, idx) => (
                  <span
                    key={idx}
                    className="text-gray-500 text-[10px] bg-gray-100 px-2 py-0.5 rounded-full"
                  >
                    {feature}
                  </span>
                ))}
              </div>

              {/* Book Button */}
              <button className="w-full mt-2 bg-red-500 hover:bg-red-600 text-white py-2 rounded-xl text-sm font-medium transition-all duration-300 flex items-center justify-center gap-2 group-hover:gap-3 shadow-sm hover:shadow-md">
                Book Now
                <FaArrowRight className="text-xs" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeUp {
          0% {
            opacity: 0;
            transform: translateY(30px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-up {
          animation: fadeUp 0.6s ease-out forwards;
        }
      `}</style>
    </section>
  );
};

export default ServicesSection;